import { initializeAnalytics } from '@firebase/analytics';
import { AnalyticsBrowser } from '@segment/analytics-next';
import type {
  AnalyticsCommonProps,
  AnalyticsEvents,
  BalanceProps,
  PurchaseSource,
  TrackEvent,
} from '@sunflower-games/analytics-events';
import { logEvent, setAnalyticsCollectionEnabled } from 'firebase/analytics';
import omit from 'lodash/omit';
import pick from 'lodash/pick';

import type { Wallet } from '../../dto/wallet.types';
import { getNewLanderType } from '../../features/auth/newLanderPins';
import type { LoyaltyTierKey } from '../../stores/loyalty/types';
import type { EnumPaymentMethods } from '../../stores/payredeem/types';
import { getSecondsSinceAppMount, getSecondsSinceSessionStart } from '../../utils/appMount';
import { devError, devLog } from '../../utils/log';
import { getCookie } from '../../utils/secureCookies';
import { postRequest } from '../api';
import type { Currency } from '../auth';
import { logToNewRelic } from '../debug';
import { firebase, firebaseAuth } from '../firebase';
import { getMediaForceTransactionId, sendGoogleTagFtdEvent, sendGoogleTagSuccessPayment } from '../googleTagEvent';
import { resolveFlag } from '../launchDarkly';
import { trackShellPurchase } from '../nativeBridge/shellAppsFlyer';
import {
  isAwaitingNativeFirebaseBridge,
  mayHaveNativeFirebase,
  sendShellFirebaseEvent,
  shellHasNativeFirebase,
  waitForNativeFirebaseBridge,
} from '../nativeBridge/shellFirebase';
import { confirmShell, getShellPresence } from '../nativeBridge/shellPresence';
import { platformContextService } from '../platformContext/PlatformContextService';

import { notifyAnalyticsDebugTap } from './debugTap';
import { getBrowserSessionId } from './getBrowserSessionId';
import { getTabId } from './getTabId';
import { getDevice, getScreenSize } from './helpers';
import { mapMethodToAnalytics } from './mapMethodToAnalytics';
import { mixpanel } from './mixpanel/mixpanelSdk';
import { withUrlAttribution } from './mixpanel/urlAttribution';
import { getOsInfo, resolveOsVersionFromClientHints } from './osInfo';
import type { AnalyticsExtendedCommonProps, SlotBattleEventName } from './types';

const SEGMENT_APP_KEY = import.meta.env.VITE_SEGMENT_APP_KEY;

type FirebaseDestination =
  | { kind: 'native' }
  | { kind: 'web'; service: ReturnType<typeof initializeAnalytics> }
  | { kind: 'off' };

// `eventKeys`: the event's own params, which go right after the priority props in the shell.
type FirebaseEvent = { name: string; params: Record<string, unknown>; eventKeys: string[] };

// Sent ahead of the event's own params in the shell, so the 25-param cap never cuts them.
const NATIVE_FIREBASE_PRIORITY_PROPS = [
  'user_id',
  'browser_session_id',
  'tab_session_id',
  'event_timestamp',
  'social_cc',
  'coins_usc',
  'coins_bsc',
  'coins_rsc',
] as const;

// Sent last in the shell, so the 25-param cap cuts these before session/device props.
const NATIVE_FIREBASE_LAST_PROPS: readonly string[] = [
  // GA4's app stream records the device's OS on its own.
  'os_name',
  'os_version',
  'facebook_fbc',
  'facebook_fbp',
  'ip_country',
  'ip_region',
  'ip_city',
  'territory_key',
  'referrer',
  // Same value as `ip_country`, so the first to go.
  'country_code',
];

class Analytics {
  // Starts the web SDK at import, as before, so gtag.js loads early; a possible native-Firebase shell decides later.
  private firebaseDestination: FirebaseDestination | null = mayHaveNativeFirebase()
    ? null
    : this.resolveFirebaseDestination();

  private heldFirebaseEvents: FirebaseEvent[] = [];

  // A shell running native Firebase gets GA4 events and the web SDK never starts.
  private resolveFirebaseDestination(): FirebaseDestination {
    if (shellHasNativeFirebase()) return { kind: 'native' };
    try {
      return {
        kind: 'web',
        service: initializeAnalytics(firebase, {
          config: {
            send_page_view: false,
            // https://developers.google.com/search/blog/2020/01/get-ready-for-new-samesitenone-secure?hl=en
            cookie_flags: 'max-age=7200;secure;samesite=none',
          },
        }),
      };
    } catch (error) {
      // Off for the page rather than retried on every event; Segment and Mixpanel are unaffected.
      logToNewRelic(
        'Analytics.initializeAnalytics',
        { message: 'Firebase analytics init failed', error: String(error) },
        'error',
      );
      return { kind: 'off' };
    }
  }

  // No destination yet, and the shell may still turn out to run native Firebase: it has not answered, or its
  // user-agent marker is here but its bridge global is not.
  private isFirebaseDestinationUndecided(): boolean {
    return this.firebaseDestination === null && (getShellPresence() === 'pending' || isAwaitingNativeFirebaseBridge());
  }

  // Decided once shell presence settles; events wait until then rather than start a web SDK the shell would double-count.
  private sendFirebaseEvent(event: FirebaseEvent) {
    // A non-empty queue also holds, so nothing fired between settle and flush overtakes it.
    if (this.heldFirebaseEvents.length > 0 || this.isFirebaseDestinationUndecided()) {
      if (this.heldFirebaseEvents.length === 0) this.flushHeldFirebaseEventsOnSettle();
      this.heldFirebaseEvents.push(event);
      return;
    }
    this.firebaseDestination ??= this.resolveFirebaseDestination();
    if (this.firebaseDestination.kind !== 'native' && shellHasNativeFirebase()) {
      this.switchToNativeFirebase(this.firebaseDestination);
    }
    this.deliverFirebaseEvent(this.firebaseDestination, event);
  }

  private flushHeldFirebaseEventsOnSettle() {
    void waitForNativeFirebaseBridge()
      .then(() => confirmShell())
      .then(() => this.flushHeldFirebaseEvents());
    // `app_close` goes out on beforeunload; still held when the page goes, it would be lost.
    window.addEventListener('pagehide', () => this.flushHeldFirebaseEvents(), { once: true });
  }

  // The web SDK started (or failed to) before the shell's bridge was injected. Events aren't doubled, but if it already
  // sent a hit the session is counted in both streams (web session_start/first_visit); the warn below is its only trace.
  private switchToNativeFirebase(from: Exclude<FirebaseDestination, { kind: 'native' }>) {
    // Also stops the web SDK's own session/engagement pings, and drops events still queued for gtag.js.
    if (from.kind === 'web') setAnalyticsCollectionEnabled(from.service, false);
    this.firebaseDestination = { kind: 'native' };
    logToNewRelic(
      'Analytics.firebaseDestination',
      {
        message:
          from.kind === 'web'
            ? 'web Firebase SDK started in a native-Firebase shell, switched to native; events not yet sent by gtag.js are lost'
            : 'web Firebase SDK failed in a native-Firebase shell, switched to native',
      },
      'warn',
    );
  }

  private flushHeldFirebaseEvents() {
    const destination = (this.firebaseDestination ??= this.resolveFirebaseDestination());
    const held = this.heldFirebaseEvents;
    this.heldFirebaseEvents = [];
    held.forEach((event) => {
      // One failing event must not drop the rest of the queue.
      try {
        this.deliverFirebaseEvent(destination, event);
      } catch (error) {
        logToNewRelic('Analytics.flushHeldFirebaseEvents', { name: event.name, error: String(error) }, 'warn');
      }
    });
  }

  private deliverFirebaseEvent(destination: FirebaseDestination, { name, params, eventKeys }: FirebaseEvent) {
    if (destination.kind === 'native') {
      sendShellFirebaseEvent(name, {
        ...pick(params, NATIVE_FIREBASE_PRIORITY_PROPS),
        ...pick(params, eventKeys),
        ...omit(params, NATIVE_FIREBASE_LAST_PROPS),
        ...pick(params, NATIVE_FIREBASE_LAST_PROPS),
      });
    } else if (destination.kind === 'web') {
      logEvent<string>(destination.service, name, params);
    }
  }

  private segmentAnalytics = new AnalyticsBrowser();

  private isIdentified = false;

  public init() {
    void resolveOsVersionFromClientHints();
    try {
      if (SEGMENT_APP_KEY) {
        this.setCommonProps({
          facebook_fbc: getCookie('_fbc'),
          facebook_fbp: getCookie('_fbp'),
        });
        void this.segmentAnalytics.load(
          {
            writeKey: SEGMENT_APP_KEY,
          },
          {
            highEntropyValuesClientHints: [
              'architecture',
              'bitness',
              'model',
              'platformVersion',
              'uaFullVersion',
              'fullVersionList',
              'wow64',
            ],
          },
        );
        getBrowserSessionId();
      } else {
        logToNewRelic('Segment.init', 'Segment app key is not set', 'error');
      }
    } catch (e) {
      logToNewRelic('Segment.init', 'Failed to initialize segment', 'error');
    }
    // Second destination while we migrate off Segment — gated on its own token, independent of Segment's key.
    mixpanel.init();
    // Start the web SDK early so its first_visit/session_start aren't lost; an undecided shell still waits.
    if (this.firebaseDestination === null && !this.isFirebaseDestinationUndecided()) {
      this.firebaseDestination = this.resolveFirebaseDestination();
    }
  }

  public async identify(firebaseId: string) {
    if (!this.isIdentified) {
      try {
        void mixpanel.identify(firebaseId);
        await this.segmentAnalytics.identify(firebaseId, null, { app: { version: __APP_VERSION__ } });
        this.isIdentified = true;
      } catch (error) {
        logToNewRelic('Segment.identify', 'Failed to identify user for segment', 'error');
      }
    }
  }

  public async logout() {
    if (this.isIdentified) {
      try {
        void mixpanel.reset();
        await this.segmentAnalytics.reset();
        this.isIdentified = false;
      } catch (error) {
        logToNewRelic('Segment.logout', 'Failed to logout user', 'error');
      }
    }
  }

  private commonProps: Partial<AnalyticsCommonProps> = {
    lobby_type: 'CC',
  };

  public setCommonProps(props: Partial<AnalyticsCommonProps>) {
    this.commonProps = {
      ...this.commonProps,
      ...props,
    };
  }

  private balanceProps: BalanceProps = {
    social_cc: 0,
    coins_usc: 0,
    coins_bsc: 0,
    coins_rsc: 0,
  };

  public setBalanceProps(props: Wallet) {
    this.balanceProps = {
      social_cc: props.gcCoin,
      coins_rsc: props.scCoin.wsc,
      coins_bsc: props.scCoin.bsc,
      coins_usc: props.scCoin.psc,
    };
  }

  public formatCommonProps(): Partial<AnalyticsExtendedCommonProps> {
    return {
      ...this.commonProps,
      referrer: document.referrer,
      device: getDevice(),
      platform: platformContextService.getConfig().platform,
      screen_size: getScreenSize(),
      ...getOsInfo(),
      user_id: firebaseAuth.currentUser?.uid,
      browser_session_id: getBrowserSessionId(),
      tab_session_id: getTabId(),
      session_time_secs: getSecondsSinceSessionStart(),
      since_app_mount_secs: getSecondsSinceAppMount(),
      event_timestamp: new Date().toISOString(),
    };
  }

  /** `source` and `landing_type` for the sign up step events of the redesigned marketing landers. */
  private getMarketingLanderProps(
    eventName: string,
    params?: { event_category?: string },
  ): { source?: string; landing_type?: string } {
    if (eventName !== 'sign_up' && params?.event_category !== 'sign_up') {
      return {};
    }

    const landingType = getNewLanderType();

    return landingType ? { source: 'marketing_lander', landing_type: landingType } : {};
  }

  public sendEvent: TrackEvent = async (eventName, paramsArg) => {
    const params = {
      ...this.formatCommonProps(),
      ...this.balanceProps,
      ...this.getMarketingLanderProps(String(eventName), paramsArg),
      ...paramsArg,
    };
    devLog(
      'sendEvent',
      `Event: ${eventName}${params.step_name ? `, Step: ${params.step_name}` : ''}${params.event_action ? `, Action: ${params.event_action}` : ''}`,
      params,
    );
    notifyAnalyticsDebugTap(String(eventName), params);
    // A GA4 failure must not cost Segment and Mixpanel this event.
    try {
      this.sendFirebaseEvent({ name: String(eventName), params, eventKeys: Object.keys(paramsArg ?? {}) });
    } catch (error) {
      logToNewRelic('Analytics.sendFirebaseEvent', { name: String(eventName), error: String(error) }, 'warn');
    }
    void mixpanel.track(String(eventName), withUrlAttribution(params));
    await this.segmentAnalytics.track(String(eventName), params);
  };

  // Minigames fire distinctly-named events (the contract's event_name), not the legacy 'widget' bucket,
  // so the name is dynamic and isn't a key in AnalyticsEvents. Delegate to sendEvent so the common-prop
  // and balance enrichment lives in one place; the cast only loosens the keyed-name constraint.
  public sendMiniGameEvent = (
    eventName: string,
    params: {
      event_parent?: 'minigames';
      event_category?: string;
      event_action?: 'impression' | 'click' | 'spin_end';
      event_type?: string;
      event_value?: string;
      button_text?: string;
    },
  ) => this.sendEvent(eventName as keyof AnalyticsEvents, params as AnalyticsEvents[keyof AnalyticsEvents]);

  // Redeem-redesign taxonomy (redeem_page/redeem/cancel_request/…) is not yet keyed in
  // AnalyticsEvents. Same pattern as sendMiniGameEvent: keep the cast in one place until the
  // shared @sunflower-games/analytics-events package defines these events.
  public sendRedeemEvent = (
    eventName: string,
    params: {
      event_parent?: 'redeem';
      event_category?: 'redeem' | 'redeem_cash';
      event_action?: 'impression' | 'click';
      event_type?: 'coin_type' | 'payment_platform' | 'redeem_amount';
      event_value?: string;
    },
  ) => this.sendEvent(eventName as keyof AnalyticsEvents, params as AnalyticsEvents[keyof AnalyticsEvents]);

  // Stamp Card's taxonomy (widget/popup/popup_close/info under event_parent 'features',
  // event_category 'stamp_card') isn't keyed in the shared @sunflower-games/analytics-events package
  // yet. Same pattern as sendMiniGameEvent/sendRedeemEvent: keep the cast in one place, and keep the
  // spec's four event names and their allowed properties enforced by this signature.
  public sendStampCardEvent = (
    eventName: 'widget' | 'popup' | 'popup_close' | 'info',
    params: {
      event_parent: 'features';
      event_category: 'stamp_card';
      event_action: 'impression' | 'click';
      click_location?: 'lobby' | 'store';
      /** Paired with `event_value` — the spec carries stamp progress as a type/value pair. */
      event_type?: 'stamps_collected';
      event_value?: string;
      button_text?: string;
    },
  ) => this.sendEvent(eventName as keyof AnalyticsEvents, params as AnalyticsEvents[keyof AnalyticsEvents]);

  // Location Notice (geo restriction pop-up, PLT-599) lives under the new 'geo' event_parent, which isn't
  // keyed in the shared @sunflower-games/analytics-events package yet. Same pattern as sendStampCardEvent:
  // keep the cast in one place and let this signature enforce the spec's event names and properties.
  public sendGeoEvent = (
    eventName:
      | 'location_notice'
      | 'enable_gps'
      | 'gps_result'
      | 'proceed'
      | 'contact_support'
      | 'location_notice_close',
    params: {
      event_parent: 'geo';
      event_category: 'location_notice';
      event_action: 'impression' | 'click';
      page_url: string;
      /** Paired with `event_value` — the restriction the pop-up (or the GPS result) resolved to. */
      event_type?: 'restriction_type';
      event_value?: 'social' | 'no_sc_purchase' | 'ticket' | 'allowed';
      triggered_by?: 'login' | 'signup';
      status?: 'success' | 'failure' | 'pending';
      error_reason?: 'gps_permission_denied' | 'gps_unavailable' | 'timeout' | 'unknown';
      error_message?: string;
      button_text?: 'enable gps' | 'play now' | 'play social only' | 'got it' | 'contact support';
    },
  ) => this.sendEvent(eventName as keyof AnalyticsEvents, params as AnalyticsEvents[keyof AnalyticsEvents]);

  // The password leak prompt's impression isn't keyed in @sunflower-games/analytics-events yet. Same pattern as
  // sendGeoEvent: the cast stays here and this signature fixes the event name and its properties.
  public sendPasswordLeakPromptShown = (params: { event_category: 'account'; event_action: 'impression' }) =>
    this.sendEvent(
      'password_leak_prompt_shown' as keyof AnalyticsEvents,
      params as AnalyticsEvents[keyof AnalyticsEvents],
    );

  // Battle of the Slots' taxonomy (event_parent 'tournament', categories slot_battle_popup/_page) isn't
  // keyed in the shared @sunflower-games/analytics-events package yet. Same pattern as
  // sendStampCardEvent: the cast lives here, the spec's names and properties are enforced by this
  // signature. Call sites go through `stores/slot-battle/slotBattleAnalytics`, which fills the identity.
  public sendSlotBattleEvent = (
    eventName: SlotBattleEventName,
    params: {
      event_parent: 'tournament';
      event_category: 'slot_battle_popup' | 'slot_battle_page';
      event_action: 'impression' | 'click';
      page_url: string;
      battle_id: string;
      coin_type: 'GC' | 'SC';
      slot_id?: number;
      click_location?: 'lobby' | 'in_slot';
      event_type?: 'prize_type' | 'battle_outcome' | 'slot_game';
      event_value?: string;
      button_text?: string;
      rank?: number;
      rank_reward_gc?: number;
      rank_reward_sc?: number;
      rank_reward_items?: string;
      final_rank?: number;
      reward_gc?: number;
      reward_sc?: number;
      reward_items?: string;
      progress?: number;
      threshold?: number;
    },
  ) => this.sendEvent(eventName as keyof AnalyticsEvents, params as AnalyticsEvents[keyof AnalyticsEvents]);

  public sendFailedPurchaseEvent(data?: any) {
    void this.sendEvent('purchase', {
      ...data,
      status: 0,
    });
  }

  public sendSuccessPurchaseEvent(data: {
    currency: Currency;
    amount: number;
    packageId: number;
    source: PurchaseSource;
    store_key: string;
    sign_up_date: string;
    ftd: boolean;
    isPvpPurchase: boolean;
    eventParams: {
      transaction_id: string | number;
      payment_method: EnumPaymentMethods | null;
      social_cc_package: number;
      coins_usc_package: number;
      store_segment: LoyaltyTierKey;
    };
    gtagParams: Record<string, any>;
  }) {
    try {
      const { purchase: purchaseFF, ftd: ftdFF } = resolveFlag('purchase_event_debug', {
        purchase: 'both',
        ftd: 'both',
      });

      logToNewRelic(
        'sendSuccessPurchaseEvent',
        {
          message: 'event start',
          data,
        },
        'info',
      );

      // AppsFlyer in the shell, independent of purchase_event_debug, which picks web destinations.
      trackShellPurchase({
        userId: firebaseAuth.currentUser?.uid,
        transactionId: String(data.eventParams.transaction_id),
        amount: data.amount,
        currency: data.currency,
        signUpDate: data.sign_up_date,
        ftd: data.ftd,
      });

      if (!purchaseFF || purchaseFF === 'both' || purchaseFF === 'google_tag') {
        // google tag
        sendGoogleTagSuccessPayment({
          ...data.gtagParams,
          ftd: data.ftd,
          value: data.amount,
          currency: data.currency,
          isPvpPurchase: data.isPvpPurchase,
          sign_up_date: data.sign_up_date,
        });
      }
      if (!purchaseFF || purchaseFF === 'both' || purchaseFF === 'firebase') {
        // firebase analytics
        void this.sendEvent('purchase', {
          step_name: 'purchase',
          is_succeeded: 1,
          payment_method: mapMethodToAnalytics(data.eventParams.payment_method),
          package_id: data.packageId,
          transaction_id: data.eventParams.transaction_id,
          provider: 'unknown',
          store_segment: data.eventParams.store_segment,
          amount: data.amount,
          is_ftd: data.ftd ? 1 : 0,
          // GA4 requirements
          currency: data.currency,
          value: data.amount,
          source: data.source,
          store_key: data.store_key,
          sign_up_date: data.sign_up_date,
        });
      }

      if (data.ftd) {
        if (!ftdFF || ftdFF === 'both' || ftdFF === 'google_tag') {
          sendGoogleTagFtdEvent({
            ...data.gtagParams,
            ftd: data.ftd,
            value: data.amount,
            currency: data.currency,
          });
        }
        if (!ftdFF || ftdFF === 'both' || ftdFF === 'firebase') {
          // firebase analytics + segment/mixpanel
          void this.sendEvent('ftd', {
            payment_method: mapMethodToAnalytics(data.eventParams.payment_method),
            package_id: data.packageId,
            transaction_id: data.eventParams.transaction_id,
            store_segment: data.eventParams.store_segment,
            amount: data.amount,
            // GA4 requirements
            currency: data.currency,
            value: data.amount,
            source: data.source,
            store_key: data.store_key,
            sign_up_date: data.sign_up_date,
          });
        }

        const mediaForceTransactionId = getMediaForceTransactionId();
        if (mediaForceTransactionId) {
          this.sendMediaForceEvent('ftd', {
            transactionId: mediaForceTransactionId,
          });
        }
      }

      logToNewRelic('sendSuccessPurchaseEvent', { message: 'event end' }, 'info', { isFtd: data.ftd });
    } catch (e) {
      logToNewRelic(
        'sendSuccessPurchaseEvent',
        {
          message: 'event fail',
          data: JSON.stringify(e, null, 2),
        },
        'error',
      );
      devError('sendSuccessPurchaseEvent error', e);
    }
  }

  sendMediaForceEvent(eventName: 'sign_up' | 'ftd', eventData: Record<'transactionId', string>) {
    try {
      void postRequest('/v2/user/mediaForceEvent', {
        eventName,
        eventData: {
          transactionId: eventData.transactionId,
        },
      });
    } catch (e) {
      logToNewRelic('sendMediaForceEvent', 'MediaForce event failed to send', 'debug');
    }
  }

  getSegmentAnonymousId() {
    return this.segmentAnalytics.instance?.user().anonymousId();
  }
}

export const analytics = new Analytics();
